import { SectionItem } from '@features/shift-post-v2/components'
import { Colors, Text } from '@pinchenterprisesnpm/friday-ui'
import React, { useRef } from 'react'
import { FormatOptionLabel } from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/components/format-option-label/format-option-label'
import { debouncedLoadLocationOptions } from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/location-section.utils'
import { useModal } from '@src/hooks/useModal'
import { EditArrivalInstructionsModal } from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/modals/edit-arrival-instructions-modal/edit-arrival-instructions-modal'
import { connect } from 'formik'
import { useDispatch, useSelector, useStore } from 'react-redux'
import {
  selectShiftPostV2CopyEditInitialValues,
  selectShiftPostV2InitialValues,
} from '@src/features/shift-post-v2/state/shift-post-v2.selectors'
import { selectBusinessEntity } from '@src/data/BusinessEntity/selectors'
import { InfoParagraph } from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/location-section.styles'
import { ShiftPostPage } from '@src/features/shift-post-v2/pages'
import { ShiftPostStep } from '@src/features/shift-post-v2/steps/steps.types'
import { SeeArrivalInstructionsModal } from '@src/features/shift-post-v2/steps/shift-details-step/components/location-section/modals/see-arrival-instructions-modal/see-arrival-instructions-modal'
import { USER_ROLE } from '@src/models/User'
import AuthService from '@src/services/AuthService'
import { getBusinessEntity } from '@src/data/BusinessEntity/thunks'
import { CustomAsyncSelect } from './components/custom-async-select'
import { ILocationSectionProps, TLocationDropdownOption } from './location-section.types'

export const LocationSection = connect<ILocationSectionProps>(
  ({ sourceStep, formik, sourcePage }) => {
    const isConfirmStep = sourceStep === ShiftPostStep.CONFIRM
    const [isOpen, toggleModal, closeModal] = useModal()
    const [
      isSeeArrivalInstructionsOpen,
      toggleSeeArrivalInstructionsModal,
      closeSeeArrivalInstructionsModal,
    ] = useModal()

    const { setFieldValue, errors, values } = formik

    const copyEditInitialValues = useSelector(selectShiftPostV2CopyEditInitialValues)
    const postInitialValues = useSelector(selectShiftPostV2InitialValues)
    const businessEntity = useSelector(selectBusinessEntity)

    const dispatch = useDispatch()
    const store = useStore()
    /**
     * Field editing is disabled if template is not selected.
     */
    const editingDisabled = sourcePage === ShiftPostPage.POST_SHIFT && !values.template_id

    const locationNotSelected = !values.location

    const validationTimeout = useRef<NodeJS.Timeout | null>(null)

    // Create a wrapper function that includes the business_id
    const loadLocationOptionsWithBusinessId = (inputValue: string, callback: any) => {
      const currentUserRole = AuthService.getLoggedInUserRole()
      const isSuperAdmin =
        currentUserRole === USER_ROLE.ACCOUNT_MANAGER_LEVEL_1 ||
        currentUserRole === USER_ROLE.ACCOUNT_MANAGER_LEVEL_2 ||
        currentUserRole === USER_ROLE.ADMIN

      // For Super Admin, AM1 and AM2 users, don't send businessEntity.id (they should see all locations)
      if (isSuperAdmin && sourcePage !== ShiftPostPage.POST_SHIFT) {
        debouncedLoadLocationOptions(inputValue, callback)
      } else {
        // Only make the API call if businessEntity is loaded
        if (businessEntity?.id) {
          debouncedLoadLocationOptions(inputValue, callback, businessEntity.id)
        } else {
          // If businessEntity is not loaded yet, return empty options
          callback([])
        }
      }
    }

    const onEdit = () => {
      toggleModal()
    }

    const handleLocationChange = async (
      location: TLocationDropdownOption | null | undefined,
      customArrivalInstructions?: string
    ) => {
      // If the newly selected location is from a different business entity,
      // we need to re-fetch the business entity in order to have their gig_identifier_policy.
      if (
        location &&
        location.business_id &&
        businessEntity?.id &&
        businessEntity.id !== location.business_id
      ) {
        await dispatch(
          getBusinessEntity(location.business_id, {
            include: ['gig_identifier_policy', 'business_setting', 'industry'],
          })
        )

        // Get the updated business entity from Redux store after the await
        const updatedBusinessEntity = selectBusinessEntity(store.getState())

        if (updatedBusinessEntity) {
          if (updatedBusinessEntity.auto_accept && !(values as any).auto_accept) {
            ;(setFieldValue as any)('auto_accept', true)
          }

          ;(setFieldValue as any)('gig_identifier', '')

          formik.setErrors({})
        }
      }
      const arrivalInstructions =
        customArrivalInstructions || location?.property_arrival_instructions || ''

      setFieldValue('property_id', location?.value)
      setFieldValue('commission_percent', location?.commission_percent)
      setFieldValue('location', {
        ...(location ?? {}),
        // Ensure property_arrival_instructions is never null to prevent HTML processing errors
        property_arrival_instructions: location?.property_arrival_instructions || '',
        ...(customArrivalInstructions && {
          property_arrival_instructions: customArrivalInstructions,
        }),
      })
      setFieldValue('min_wage', location?.min_wage)
      setFieldValue('suggested_pay_rate', location?.suggested_pay_rate)
      setFieldValue('property_timezone', location?.timezone)
      setFieldValue('property_arrival_instructions', arrivalInstructions)

      // Clear any existing timeout to prevent potential memory leaks
      if (validationTimeout.current) {
        clearTimeout(validationTimeout.current)
      }

      // Set new timeout for pay_value validation
      // Validation without setTimeout won't work, as it will batch setFieldValue and validateField calls
      // With setTimeout, we ensure validateField is done in another tick of the JS event loop.
      // Should work with 0 timeout, but adding 200ms delay just in case
      validationTimeout.current = setTimeout(() => {
        formik.validateField('pay_value')
        // Also re-validate gig_identifier to clear any lingering errors
        formik.validateField('gig_identifier')
      }, 200)
    }

    return (
      <SectionItem
        title='Location'
        hasEdit={true}
        isEditDisabled={locationNotSelected || editingDisabled}
        editDisabledTooltip='Please select a Location first so you can edit the Arrival Instructions.'
        onEdit={onEdit}
        onShowFullDescription={isConfirmStep ? toggleSeeArrivalInstructionsModal : undefined}
        showFullDescriptionTitle='See Arrival Instructions'
      >
        {isConfirmStep && (
          <Text fontSize='medium' mt='xs' mb='0' color={Colors.dark}>
            {values.location?.name ?? '/'}
          </Text>
        )}

        {!isConfirmStep && (
          <>
            {businessEntity?.id ? (
              <CustomAsyncSelect
                label='Location'
                value={values.location}
                placeholder='Please select a location...'
                isOutlined
                rounded={true}
                size='large'
                loadOptions={loadLocationOptionsWithBusinessId}
                defaultOptions={true}
                my='sm'
                onChange={(location) => handleLocationChange(location)}
                key={values.keyForLocationsDropdown}
                errorMessage={
                  errors.property_id && !values.property_id ? errors.property_id : undefined
                }
                formatOptionLabel={(value: TLocationDropdownOption) => (
                  <FormatOptionLabel value={value} />
                )}
                isDisabled={editingDisabled}
              />
            ) : (
              <div style={{ padding: '16px', textAlign: 'center', color: '#666' }}>
                Loading locations...
              </div>
            )}

            <Text my={0} fontSize='medium'>
              Arrival Instructions
            </Text>

            {values.location ? (
              values.location?.property_arrival_instructions ? (
                <InfoParagraph
                  dangerouslySetInnerHTML={{
                    __html:
                      (values.location?.property_arrival_instructions ?? 'None Provided') ||
                      'None Provided',
                  }}
                />
              ) : (
                <Text mb={0} mt='sm' color='danger' fontSize='medium'>
                  None Provided
                </Text>
              )
            ) : (
              <Text mb={0} mt='sm' color='muted'>
                Please select a location first to edit the arrival instructions.
              </Text>
            )}
          </>
        )}

        <EditArrivalInstructionsModal
          isOpen={isOpen}
          closeModal={closeModal}
          isConfirmStep={isConfirmStep}
          handleLocationChange={handleLocationChange}
          sourcePage={sourcePage}
        />

        <SeeArrivalInstructionsModal
          isOpen={isSeeArrivalInstructionsOpen}
          closeModal={closeSeeArrivalInstructionsModal}
          formik={formik}
        />
      </SectionItem>
    )
  }
)
